import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent } from "react";
import type { FieldDefinition, ResolvedRef } from "../../../lib/types";
import { ReferenceField, ReferencesField } from "./ReferenceFields";

/**
 * Sprint 12 — one inline editor for every field type, rendered in the vertical
 * field list. Text-like types use click-to-edit (click the value → input →
 * Enter/blur saves, Esc cancels); enum is an always-on select; reference/
 * references delegate to the chip editors. Collapsing the per-type editors into
 * one switch keeps the surface small without losing type-appropriate inputs.
 */

interface FieldEditorProps {
  field: FieldDefinition;
  value: unknown;
  /** Resolved forward references — used for reference/references display. */
  refs?: ResolvedRef[];
  onSave: (value: unknown) => void;
  onOpenRef?: (entityTypeSlug: string, entityId: string) => void;
}

const inputBase: CSSProperties = {
  width: "100%",
  padding: "5px 8px",
  fontSize: 13,
  fontFamily: "var(--font-sans)",
  border: "0.5px solid var(--c-pink-600)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-primary)",
  outline: "none",
};

const displayStyle: CSSProperties = {
  fontSize: 13,
  color: "var(--color-text-primary)",
  lineHeight: 1.5,
  cursor: "text",
  padding: "5px 8px",
  borderRadius: "var(--border-radius-sm)",
  minHeight: 18,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

const placeholderStyle: CSSProperties = {
  ...displayStyle,
  color: "var(--color-text-tertiary)",
};

export function FieldEditor({ field, value, refs = [], onSave, onOpenRef }: FieldEditorProps) {
  // --- Reference types: chip editors, no click-to-edit ---
  if (field.type === "reference") {
    return (
      <ReferenceField
        targetSlug={field.target ?? ""}
        refs={refs}
        onSave={(v) => onSave(v)}
        onOpenRef={onOpenRef}
      />
    );
  }
  if (field.type === "references") {
    return (
      <ReferencesField
        targetSlug={field.target ?? ""}
        refs={refs}
        onSave={(v) => onSave(v)}
        onOpenRef={onOpenRef}
      />
    );
  }

  // --- Enum: always-on select ---
  if (field.type === "enum") {
    return (
      <select
        value={value == null ? "" : String(value)}
        onChange={(e) => onSave(e.target.value === "" ? null : e.target.value)}
        className="kuvio-panel-row"
        style={{ ...inputBase, border: "0.5px solid var(--color-border-secondary)", cursor: "pointer", maxWidth: 280 }}
      >
        <option value="">—</option>
        {(field.choices ?? []).map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
    );
  }

  return <ScalarEditor field={field} value={value} onSave={onSave} />;
}

function ScalarEditor({
  field,
  value,
  onSave,
}: {
  field: FieldDefinition;
  value: unknown;
  onSave: (value: unknown) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const ref = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  const multiline = field.type === "richtext" || field.type === "document";

  useEffect(() => {
    if (editing) {
      ref.current?.focus();
      if (ref.current instanceof HTMLInputElement) ref.current.select();
    }
  }, [editing]);

  function begin() {
    setDraft(value == null ? "" : String(value));
    setEditing(true);
  }

  function commit() {
    setEditing(false);
    const trimmed = field.type === "number" || field.type === "currency" ? draft.trim() : draft;
    if ((value == null ? "" : String(value)) === trimmed) return; // unchanged
    onSave(parseValue(trimmed, field));
  }

  function handleKey(e: KeyboardEvent) {
    if (e.key === "Escape") {
      e.preventDefault();
      setEditing(false);
    } else if (e.key === "Enter" && !multiline) {
      e.preventDefault();
      commit();
    }
  }

  if (editing) {
    if (multiline) {
      return (
        <textarea
          ref={ref as React.RefObject<HTMLTextAreaElement>}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={handleKey}
          rows={4}
          style={{ ...inputBase, resize: "vertical", lineHeight: 1.5 }}
        />
      );
    }
    return (
      <input
        ref={ref as React.RefObject<HTMLInputElement>}
        type={inputType(field)}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKey}
        style={{ ...inputBase, maxWidth: multiline ? "100%" : 320 }}
      />
    );
  }

  const display = formatValue(value, field);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={begin}
      onKeyDown={(e) => {
        if (e.key === "Enter") begin();
      }}
      className="kuvio-panel-row"
      style={display === null ? placeholderStyle : displayStyle}
    >
      {display ?? "Empty"}
    </div>
  );
}

function inputType(field: FieldDefinition): string {
  switch (field.type) {
    case "number":
    case "currency":
      return "number";
    case "date":
      return "date";
    case "datetime":
      return "datetime-local";
    case "email":
      return "email";
    case "phone":
      return "tel";
    default:
      return "text";
  }
}

function parseValue(draft: string, field: FieldDefinition): unknown {
  if (draft === "") return null;
  if (field.type === "number" || field.type === "currency") {
    const n = parseFloat(draft.replace(/[,$]/g, ""));
    return isNaN(n) ? draft : n;
  }
  return draft;
}

/** Returns the formatted display string, or null when the field is empty. */
function formatValue(value: unknown, field: FieldDefinition): string | null {
  if (value === null || value === undefined || value === "") return null;
  if (field.type === "currency") {
    const n = typeof value === "number" ? value : parseFloat(String(value));
    if (!isNaN(n)) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(n);
    }
  }
  if (field.type === "number") {
    const n = typeof value === "number" ? value : parseFloat(String(value));
    if (!isNaN(n)) return new Intl.NumberFormat("en-US").format(n);
  }
  if (field.type === "date" || field.type === "datetime") {
    const d = new Date(String(value));
    if (!isNaN(d.getTime())) {
      const opts: Intl.DateTimeFormatOptions = { year: "numeric", month: "short", day: "numeric" };
      if (field.type === "datetime") {
        opts.hour = "numeric";
        opts.minute = "2-digit";
      }
      return d.toLocaleDateString("en-US", opts);
    }
  }
  return String(value);
}
